import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";

import type { AcqCadence, AcqCatalogItem } from "../../api/acqService";
import { CADENCE_LABEL } from "../../model/acqLabels";
import { discoverPriceText } from "../../model/useDiscoverPrice";

const CADENCES: AcqCadence[] = ["DAILY", "WEEKLY"];

/**
 * 「订阅自动找」的确认框。
 *
 * ★★ 它存在的全部理由是**把定期扣分这件事说在前面**:订阅 = 以后每天/每周一自动跑一次,
 *   每次都扣。不弹这个框直接拨开关,用户第二周看到账单才知道自己签了什么 ——
 *   那正是「新视线 108 次搜索 216 积分 0 线索然后停号」的形状。
 * ★ 取消订阅**不弹框**:关掉一个会花钱的东西不需要确认(确认框只拦"开始花钱"那一侧)。
 * ★ 价一律从 `priceCredits` 来,取不到写「按次计费」,**永不编数**。
 */
export function SubscribeDialog({
  item,
  productName,
  price,
  saving,
  onOpenChange,
  onConfirm,
}: {
  item: AcqCatalogItem | null;
  /**
   * 选中的产品名(契约 §17.3.1);`null` = 停在「全部」上,订的是整条线。
   * ★ 必须写进这个框:这一版订阅按产品记,而用户点的那个开关长得和以前一模一样 ——
   *   不说清楚他会以为自己订的还是整条线,第二天看到只有一个产品的单子会以为漏了。
   */
  productName?: string | null;
  price: number | null;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (cadence: AcqCadence) => void;
}) {
  const [cadence, setCadence] = useState<AcqCadence>("DAILY");

  useEffect(() => {
    if (item) setCadence(item.cadence);
  }, [item]);

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {productName ? `只为「${productName}」自动找 · ` : "订阅自动找 · "}
            {item?.name ?? ""}
          </DialogTitle>
          <DialogDescription>
            以后不用自己点。凌晨自动跑一轮，有单子早上推给你。
            {productName ? `这一轮只用「${productName}」的词跑。` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {CADENCES.map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={cadence === c}
                onClick={() => setCadence(c)}
                className={cn(
                  "rounded-lg border px-3 py-2.5 text-left transition-colors",
                  cadence === c ? "border-primary bg-primary/5" : "hover:bg-accent/50"
                )}
              >
                <span className="block text-sm font-semibold">{CADENCE_LABEL[c]}</span>
                <span className="mt-0.5 block text-2xs text-muted-foreground">
                  {c === "DAILY" ? "每天凌晨跑一轮" : "每周一凌晨跑一轮"}
                </span>
              </button>
            ))}
          </div>

          {/* ★ 把"会重复扣"写成一句人能算的账,而不是一个价签。 */}
          <p className="rounded-lg border border-dashed border-warning/50 bg-warning/5 px-3 py-2.5 text-xs leading-relaxed">
            <span className="font-bold text-warning">每次约 {discoverPriceText(price)}</span>，
            {cadence === "DAILY" ? "每天" : "每周一"}扣一次。
            <span className="font-semibold">一条都没发现的那轮不扣</span>
            ，随时可以关掉。
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            再想想
          </Button>
          <Button onClick={() => onConfirm(cadence)} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            就这么订
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
